import { IMPERSONATION_BAR_HEIGHT_PX } from 'src/components/dashboard/impersonation-bar-height';

import type {
  ClientPortalMobileElementName,
  ClientPortalNavElementName,
  ClientPortalNavGroup,
  ClientPortalNavId,
  ClientPortalNavItem,
  ClientPortalNavRetryElementName,
  ClientPortalUserMenuElementName,
} from './types';
import {
  NavCompass,
  NavFileText,
  NavHouse,
  NavLandmark,
  NavListChecks,
  NavReceipt,
  NavTrendingUp,
  NavUsers,
} from './nav-icons';

export const CLIENT_PORTAL_HOME_ROUTE = '/dashboard';
export const CLIENT_PORTAL_ACCOUNT_ROUTE = '/dashboard/account';
export const CLIENT_PORTAL_TASKS_ROUTE = '/tasks';
export const CLIENT_PORTAL_SHARED_MATERIALS_ROUTE = '/dashboard/reports';
export const CLIENT_PORTAL_FILES_ROUTE = '/files';

export const CLIENT_PORTAL_NAV_GROUPS: readonly ClientPortalNavGroup[] = [
  {
    label: null,
    items: [
      { id: 'home', label: 'Home', icon: NavHouse },
      { id: 'yourTeam', label: 'Your team', icon: NavUsers },
      { id: 'tasks', label: 'Tasks', icon: NavListChecks, showBadge: true },
    ],
  },
  {
    label: 'Wealth',
    items: [
      { id: 'investments', label: 'Investments', icon: NavTrendingUp },
      { id: 'accounts', label: 'Net worth', icon: NavLandmark },
    ],
  },
  {
    label: 'Planning',
    items: [
      { id: 'tax', label: 'Tax preparation', icon: NavReceipt },
      { id: 'planning', label: 'Planning', icon: NavCompass },
    ],
  },
  {
    label: 'Library',
    items: [{ id: 'documents', label: 'Documents', icon: NavFileText }],
  },
] as const;

export const CLIENT_PORTAL_MOBILE_PRIMARY_NAV_IDS: readonly ClientPortalNavId[] = [
  'home',
  'investments',
  'accounts',
  'tasks',
] as const;

const CLIENT_PORTAL_NAV_ITEMS: readonly ClientPortalNavItem[] = CLIENT_PORTAL_NAV_GROUPS.flatMap(
  (group) => group.items,
);

export const CLIENT_PORTAL_MOBILE_PRIMARY_NAV_ITEMS: readonly ClientPortalNavItem[] =
  CLIENT_PORTAL_MOBILE_PRIMARY_NAV_IDS.flatMap((id) => CLIENT_PORTAL_NAV_ITEMS.filter((item) => item.id === id));

export const CLIENT_PORTAL_MOBILE_OVERFLOW_NAV_ITEMS: readonly ClientPortalNavItem[] = CLIENT_PORTAL_NAV_ITEMS.filter(
  (item) => !CLIENT_PORTAL_MOBILE_PRIMARY_NAV_IDS.includes(item.id),
);

export const CLIENT_PORTAL_MOBILE_MORE_LABEL = 'More';

export const CLIENT_PORTAL_MOBILE_MORE_SHEET_TITLE = 'More';

export const CLIENT_PORTAL_MOBILE_MORE_CLOSE_LABEL = 'Close menu';

export const CLIENT_PORTAL_SWITCH_HOUSEHOLD_LABEL = 'Switch household';

export const CLIENT_PORTAL_SETTINGS_LABEL = 'Settings';

export const CLIENT_PORTAL_SIGN_OUT_LABEL = 'Sign out';

export const CLIENT_PORTAL_MOBILE_MORE_ELEMENT_NAME: ClientPortalMobileElementName = 'client-portal-mobile-more-button';

export const CLIENT_PORTAL_MOBILE_BACK_ELEMENT_NAME: ClientPortalMobileElementName = 'client-portal-mobile-back-link';

export const CLIENT_PORTAL_NAV_LABELS: Readonly<Record<ClientPortalNavId, string>> = {
  home: 'Home',
  yourTeam: 'Your team',
  tasks: 'Tasks',
  investments: 'Investments',
  accounts: 'Net worth',
  tax: 'Tax preparation',
  planning: 'Planning',
  documents: 'Documents',
  account: 'Account',
};

export const CLIENT_PORTAL_NAV_ELEMENT_NAMES: Readonly<Record<ClientPortalNavId, ClientPortalNavElementName>> = {
  home: 'client-portal-nav-home-link',
  yourTeam: 'client-portal-nav-your-team-link',
  tasks: 'client-portal-nav-tasks-link',
  investments: 'client-portal-nav-investments-link',
  accounts: 'client-portal-nav-accounts-link',
  tax: 'client-portal-nav-tax-link',
  planning: 'client-portal-nav-planning-link',
  documents: 'client-portal-nav-documents-link',
  account: 'client-portal-nav-account-link',
};

export const CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES: Readonly<
  Record<'settings' | 'logout' | 'switchHousehold', ClientPortalUserMenuElementName>
> = {
  settings: 'client-portal-user-menu-settings-link',
  logout: 'client-portal-user-menu-logout-button',
  switchHousehold: 'client-portal-user-menu-switch-household-item',
};

export type ClientPortalNavRoute = {
  readonly navId: ClientPortalNavId;
  readonly prefix: string;
  readonly sectionHref: string | null;
};

export const CLIENT_PORTAL_NAV_ROUTES: readonly ClientPortalNavRoute[] = [
  { navId: 'yourTeam', prefix: '/dashboard/advisor', sectionHref: '/dashboard/advisor' },
  { navId: 'tasks', prefix: CLIENT_PORTAL_TASKS_ROUTE, sectionHref: CLIENT_PORTAL_TASKS_ROUTE },
  { navId: 'tax', prefix: '/dashboard/tax', sectionHref: '/dashboard/tax' },
  { navId: 'account', prefix: CLIENT_PORTAL_ACCOUNT_ROUTE, sectionHref: CLIENT_PORTAL_ACCOUNT_ROUTE },
  {
    navId: 'documents',
    prefix: CLIENT_PORTAL_SHARED_MATERIALS_ROUTE,
    sectionHref: CLIENT_PORTAL_SHARED_MATERIALS_ROUTE,
  },
  { navId: 'documents', prefix: CLIENT_PORTAL_FILES_ROUTE, sectionHref: CLIENT_PORTAL_FILES_ROUTE },
  { navId: 'investments', prefix: '/savvy-portfolio', sectionHref: null },
  { navId: 'accounts', prefix: '/portfolio', sectionHref: '/portfolio' },
  { navId: 'planning', prefix: '/financial-plan', sectionHref: '/financial-plan' },
] as const;

export const CLIENT_PORTAL_NAV_BUTTON_CLASS =
  'cursor-pointer text-secondary hover:bg-primary/5 hover:text-primary data-[active]:bg-primary/10 data-[active]:text-primary data-[active]:font-semibold';

export const CLIENT_PORTAL_SIDEBAR_CLASS =
  'bg-transparent [&_[data-slot=sidebar-inner]]:border-r-0 [&_[data-slot=sidebar-inner]]:bg-transparent';

export const CLIENT_PORTAL_SIDEBAR_TRIGGER_CLASS = 'size-8 shrink-0! text-secondary! hover:text-dark! [&_svg]:size-4!';

export const CLIENT_PORTAL_SIDEBAR_NAV_GROUP_CLASS = 'group-data-[collapsible=icon]:px-0!';

export const CLIENT_PORTAL_COMING_SOON_LABEL = 'coming soon';

export const CLIENT_PORTAL_RESTRICTED_LABEL = 'not available on your account';

export const CLIENT_PORTAL_INVESTMENTS_RESTRICTED_LABEL =
  'Your portfolio will be available for you to view after your accounts are funded.';

export const CLIENT_PORTAL_NAV_RETRY_MESSAGE = "We couldn't load your full menu.";

export const CLIENT_PORTAL_NAV_RETRY_LABEL = 'Try again';

export const CLIENT_PORTAL_NAV_RETRY_ELEMENT_NAME: ClientPortalNavRetryElementName = 'client-portal-nav-retry-button';

export const CLIENT_PORTAL_NAV_BOOTSTRAP_TIMEOUT_MS = 8_000;

export const CLIENT_PORTAL_NAV_UNAVAILABLE_CLASS = 'cursor-default aria-disabled:pointer-events-auto';

export const CLIENT_PORTAL_TASK_BADGE_CAP = 9;

export const CLIENT_PORTAL_CRUMB_LEAF_CLASS = 'truncate';

export const CLIENT_PORTAL_SURFACE_ERROR_TITLE = 'Something went wrong loading this page';

export const CLIENT_PORTAL_SURFACE_ERROR_DESCRIPTION =
  'Try reloading. If it keeps happening, reach out to your advisory team.';

export const CLIENT_PORTAL_IMPERSONATION_OFFSET_PX = IMPERSONATION_BAR_HEIGHT_PX;
